/**
 * Tour Store - Manages the state of the "What's New" tour
 * Uses Zustand with localStorage persistence to track tour completion
 */
import { create } from "zustand"
import { persist } from "zustand/middleware"

interface TourStore {
  /** Whether the user has completed the tour for the current version */
  hasSeenTour: boolean
  /** Current tour version - increment to show tour again with new features */
  tourVersion: string
  /** Whether the tour is currently active */
  isTourActive: boolean
  /** Set whether the user has seen the tour */
  setHasSeenTour: (seen: boolean) => void
  /** Set the tour active state */
  setTourActive: (active: boolean) => void
  /** Start the tour (can be triggered manually) */
  startTour: () => void
  /** Complete the tour */
  completeTour: () => void
  /** Reset tour state (for testing or forcing re-show) */
  resetTour: () => void
}

const CURRENT_TOUR_VERSION = "1.0.0"

export const useTourStore = create<TourStore>()(
  persist(
    (set) => ({
      hasSeenTour: false,
      tourVersion: CURRENT_TOUR_VERSION,
      isTourActive: false,

      setHasSeenTour: (seen: boolean) => {
        set({ hasSeenTour: seen })
      },

      setTourActive: (active: boolean) => {
        set({ isTourActive: active })
      },

      startTour: () => {
        set({ isTourActive: true })
      },

      completeTour: () => {
        set({
          hasSeenTour: true,
          isTourActive: false,
          tourVersion: CURRENT_TOUR_VERSION,
        })
      },

      resetTour: () => {
        set({
          hasSeenTour: false,
          isTourActive: false,
          tourVersion: CURRENT_TOUR_VERSION,
        })
      },
    }),
    {
      name: "dataq-tour-storage",
      // Only persist these fields
      partialize: (state) => ({
        hasSeenTour: state.hasSeenTour,
        tourVersion: state.tourVersion,
      }),
      // Check if tour version has changed
      onRehydrateStorage: () => (state) => {
        if (state && state.tourVersion !== CURRENT_TOUR_VERSION) {
          // New version detected, reset tour
          state.hasSeenTour = false
          state.tourVersion = CURRENT_TOUR_VERSION
        }
      },
    }
  )
)

export { CURRENT_TOUR_VERSION }
